import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import { useAuth } from "../core/AuthContext";
import "../styles/departments.css";
import "../styles/userlist.css";
import "../styles/general.css";
import NavHeader from "./NavHeader";
import LoadingSpinner from "./LoadingSpinner";
import CustomAlert from "./CustomAlert";
import EditDepartmentModal from "./departments/EditDepartmentModal";
import roleService from "../core/services/RoleService";
import { canEditDepartment, canEditDepartments, userHasDepartmentRole } from "../core/utils/permissionUtils";
import {
  getDepartmentColor,
  getDepartmentHeadRoleName,
  getDepartmentIcon,
  getDepartmentsForContext,
} from "../core/constants/departments";
import { useDepartments } from "../core/DepartmentsContext";

function getAssignedUserDisplayName(user) {
  if (!user) return null;
  return user.firstLastName?.trim() || user.fullName || user.name || null;
}

const DepartmentCard = memo(({ department, catalog, headUser, canEdit, onEdit, isEditMode }) => {
  const color = getDepartmentColor(department, catalog);
  const coverImage = department.coverImage;
  const headName = getAssignedUserDisplayName(headUser);
  const departmentId = department._id;
  const isNavigable = Boolean(departmentId) && !(canEdit && isEditMode);

  const cardBody = (
    <>
      <div className="department-card-cover">
        {coverImage ? (
          <img
            src={coverImage}
            alt=""
            className="department-card-cover-image"
          />
        ) : (
          <div className="department-card-icon-fallback">
            {getDepartmentIcon(
              department,
              { className: "department-card-icon" },
              catalog,
            )}
          </div>
        )}
      </div>
      <div className="department-card-info">
        <h2 className="department-card-name">{department.name}</h2>
        {headName && (
          headUser?._id ? (
            <Link
              to={`/users/${headUser._id}`}
              className="department-card-head"
              onClick={(event) => event.stopPropagation()}
            >
              {headName}
            </Link>
          ) : (
            <p className="department-card-head">{headName}</p>
          )
        )}
      </div>
    </>
  );

  return (
    <article
      className={`department-card${canEdit && isEditMode ? " department-card-editable" : ""}${isNavigable ? " department-card-navigable" : ""}`}
      style={{ "--department-color": color }}
    >
      {canEdit && isEditMode && (
        <button
          type="button"
          className="department-card-edit-button"
          onClick={() => onEdit(department)}
          aria-label={`Edit ${department.name}`}
        >
          <Icon icon="mdi:pencil" />
        </button>
      )}

      {isNavigable ? (
        <Link to={`/departments/${departmentId}`} className="department-card-link">
          {cardBody}
        </Link>
      ) : (
        cardBody
      )}
    </article>
  );
});

function DepartmentsList() {
  const { user: contextUser } = useAuth();
  const {
    departments: catalog,
    loading: departmentsLoading,
    refreshDepartments,
  } = useDepartments();
  const [roleUsersByName, setRoleUsersByName] = useState({});
  const [rolesLoading, setRolesLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [alertInfo, setAlertInfo] = useState({ show: false, type: "", message: "" });

  const canEditAllDepartments = canEditDepartments(contextUser);

  const departments = useMemo(
    () => getDepartmentsForContext("display", catalog),
    [catalog],
  );

  const canEditAnyDepartment = useMemo(
    () =>
      canEditAllDepartments ||
      departments.some((department) =>
        userHasDepartmentRole(contextUser, department, catalog),
      ),
    [canEditAllDepartments, contextUser, departments, catalog],
  );

  const canEditDepartmentCard = useCallback(
    (department) => canEditDepartment(contextUser, department, catalog),
    [contextUser, catalog],
  );

  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const roles = await roleService.getAllRoles();
      const map = {};
      (roles || []).forEach((role) => {
        if (role?.name) {
          map[role.name] = role.assignedUsers || [];
        }
      });
      setRoleUsersByName(map);
    } catch (error) {
      console.error("Failed to fetch roles for department heads:", error);
      setRoleUsersByName({});
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const getHeadUserForDepartment = (department) => {
    const roleName = getDepartmentHeadRoleName(department, catalog);
    if (!roleName) return null;
    return roleUsersByName[roleName]?.[0] || null;
  };

  const handleDepartmentSaved = async () => {
    await Promise.all([refreshDepartments(), fetchRoles()]);
    setAlertInfo({
      show: true,
      type: "success",
      message: "Department updated successfully.",
    });
  };

  const loading = departmentsLoading || rolesLoading;

  return (
    <div className="page-container departments-page-container">
      <NavHeader />

      <div className="page-title-row">
        <h1 className="page-title page-title-icon">
          <span className="main-section-icon">
            <span
              className="main-section-icon-fill"
              style={{ background: "rgba(6, 182, 212, 0.15)", color: "#06b6d4" }}
            >
              <Icon icon="mdi:domain" className="icon" />
            </span>
          </span>
          Departments
        </h1>

        {canEditAnyDepartment && (
          <div className="title-action-buttons departments-title-actions">
            <button
              type="button"
              className={`title-icon-button${isEditMode ? " active" : ""}`}
              onClick={() => setIsEditMode((prev) => !prev)}
              aria-label={isEditMode ? "Exit edit mode" : "Edit departments"}
              aria-pressed={isEditMode}
            >
              <Icon icon="mdi:pencil" />
            </button>
          </div>
        )}
      </div>

      {alertInfo.show && (
        <CustomAlert
          type={alertInfo.type}
          message={alertInfo.message}
          onClose={() => setAlertInfo({ show: false, type: "", message: "" })}
        />
      )}

      <div className="departments-page">
        {canEditAnyDepartment && (
          <div className="departments-toolbar">
            <button
              type="button"
              className={`button list-button icon-menu-button departments-edit-toggle${isEditMode ? " selected" : ""}`}
              onClick={() => setIsEditMode((prev) => !prev)}
            >
              <Icon icon="mdi:pencil" className="button-icon" />
              <span className="button-label">
                {isEditMode ? "Done editing" : "Edit departments"}
              </span>
            </button>
          </div>
        )}

        {loading && departments.length === 0 ? (
          <LoadingSpinner message="Loading departments" size="sm" />
        ) : (
          <div className="departments-grid">
            {departments.map((department) => (
              <DepartmentCard
                key={department._id || department.name}
                department={department}
                catalog={catalog}
                headUser={getHeadUserForDepartment(department)}
                canEdit={canEditDepartmentCard(department) && isEditMode}
                isEditMode={isEditMode}
                onEdit={(department) => {
                  const match = catalog.find(
                    (entry) =>
                      (department._id && String(entry._id) === String(department._id)) ||
                      entry.name === department.name,
                  );
                  setEditingDepartment(match || department);
                }}
              />
            ))}
          </div>
        )}
      </div>

      <EditDepartmentModal
        show={Boolean(editingDepartment)}
        onHide={() => setEditingDepartment(null)}
        department={editingDepartment}
        catalog={catalog}
        onSaved={handleDepartmentSaved}
      />
    </div>
  );
}

export default DepartmentsList;
